import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaBan,
  FaClock,
  FaComments,
  FaHome,
  FaLightbulb,
  FaPaintBrush,
  FaQuestion,
  FaQuestionCircle,
  FaTheaterMasks,
  FaTimes,
  FaUserSecret,
} from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import { getQRCodeById } from "../api/qrcode/index";
import DrawerPanel from "../draw/DrawerPanel";
import logo from "../assets/images/IMG_4242.PNG";

// Game type configurations
const GAME_TYPES = {
  actItUp: {
    name: "ولا كلمة",
    nameEn: "Act It Up",
    icon: FaTheaterMasks,
    gradient: "from-orange-500 to-red-500",
    gradientHover: "from-orange-600 to-red-600",
    lightBg: "from-orange-100 to-red-100",
    border: "border-orange-300",
    text: "text-orange-900",
    iconColor: "text-orange-600",
    bgLight: "bg-orange-50",
  },
  forbiddenWords: {
    name: "الكلمات الممنوعة",
    nameEn: "Forbidden Words",
    icon: FaComments,
    gradient: "from-purple-600 to-pink-600",
    gradientHover: "from-purple-700 to-pink-700",
    lightBg: "from-purple-100 to-pink-100",
    border: "border-purple-300",
    text: "text-purple-900",
    iconColor: "text-purple-600",
    bgLight: "bg-purple-50",
  },
  yesNo: {
    name: "اي لا",
    nameEn: "Yes or No",
    icon: FaQuestionCircle,
    gradient: "from-blue-500 to-cyan-500",
    gradientHover: "from-blue-600 to-cyan-600",
    lightBg: "from-blue-100 to-cyan-100",
    border: "border-blue-300",
    text: "text-blue-900",
    iconColor: "text-blue-600",
    bgLight: "bg-blue-50",
  },
  whoAmI: {
    name: "من أنا",
    nameEn: "Who Am I",
    icon: FaUserSecret,
    gradient: "from-teal-500 to-emerald-500",
    gradientHover: "from-teal-600 to-emerald-600",
    lightBg: "from-teal-100 to-emerald-100",
    border: "border-teal-300",
    text: "text-teal-900",
    iconColor: "text-teal-600",
    bgLight: "bg-teal-50",
  },
  draw: {
    name: "ارسمها",
    nameEn: "Draw It",
    icon: FaPaintBrush,
    gradient: "from-indigo-500 to-sky-500",
    gradientHover: "from-indigo-600 to-sky-600",
    lightBg: "from-indigo-100 to-sky-100",
    border: "border-indigo-300",
    text: "text-indigo-900",
    iconColor: "text-indigo-600",
    bgLight: "bg-indigo-50",
  },
};

const QRCodeGame = () => {
  const [searchParams] = useSearchParams();
  const qrCodeId = searchParams.get("qrCodeId");
  // Draw mode only: the board's live session code, present when the QR was
  // shown by a board that mirrors the drawing.
  const drawCode = searchParams.get("d");
  const [showInstructions, setShowInstructions] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30); // Timer for yesNo game
  const [timerActive, setTimerActive] = useState(false);

  // Fetch QR code data
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["qrcode", qrCodeId],
    queryFn: () => getQRCodeById(qrCodeId),
    retry: 2,
    refetchOnWindowFocus: false,
    enabled: !!qrCodeId, // Only fetch if qrCodeId exists
  });

  // Prevent right-click
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // Auto-show instructions modal on first load
  useEffect(() => {
    if (data && !showInstructions) {
      const hasSeenInstructions = sessionStorage.getItem(
        `qrcode_instructions_${qrCodeId}`
      );
      if (!hasSeenInstructions) {
        setShowInstructions(true);
        sessionStorage.setItem(`qrcode_instructions_${qrCodeId}`, "true");
      }
    }
  }, [data, qrCodeId, showInstructions]);

  // Timer effect for yesNo game
  useEffect(() => {
    if (timerActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timerActive, timeLeft]);

  // Check if qrCodeId is missing
  if (!qrCodeId) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-background via-behindButton to-background p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <FaTimes className="text-6xl text-danger mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-dark mb-3">رمز QR مفقود</h2>
          <p className="text-gray-600 mb-6">
            يرجى المسح من خلال رمز QR صحيح للوصول إلى اللعبة
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-mainButton hover:bg-mainButtonShadow text-white font-bold py-3 px-8 rounded-xl transition-all duration-300"
          >
            <FaHome />
            العودة للرئيسية
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-background via-behindButton to-background p-4">
        <img src={logo} alt="تربع" className="w-32 mb-6 animate-pulse" />
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-mainButton"></div>
        <p className="text-white text-lg mt-4">جارٍ التحميل...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-br from-background via-behindButton to-background p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <FaTimes className="text-6xl text-danger mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-dark mb-3">خطأ</h2>
          <p className="text-gray-600 mb-6">
            {error?.error ||
              error?.message ||
              "رمز QR هذا غير موجود أو غير صالح"}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-mainButton hover:bg-mainButtonShadow text-white font-bold py-3 px-8 rounded-xl transition-all duration-300"
          >
            <FaHome />
            العودة للرئيسية
          </Link>
        </div>
      </div>
    );
  }

  const qrCode = data;
  const gameType = qrCode?.gameType || "actItUp";
  const gameConfig = GAME_TYPES[gameType] || GAME_TYPES.actItUp;
  const GameIcon = gameConfig.icon;

  // Instructions Modal
  const InstructionsModal = () => {
    const getInstructions = () => {
      switch (gameType) {
        case "actItUp":
          return (
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>اقرأ الكلمة أو الجملة</li>
              <li className="font-semibold">
                مثّل الكلمة بالحركات دون أن تتكلم!
              </li>
              <li>لا يُسمح باستخدام الصوت أو الكلمات</li>
              <li>إذا خمّن فريقك الإجابة الصحيحة، تفوز بالنقطة!</li>
            </ol>
          );
        case "forbiddenWords":
          return (
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>اقرأ الكلمة أو الجملة التي يجب وصفها</li>
              <li className="text-danger font-semibold">
                ممنوع استخدام الكلمة و الكلمات المحظورة!
              </li>
              <li>اشرح المعنى لفريقك دون استخدام الكلمات الممنوعة</li>
              <li>إذا خمّن فريقك الإجابة الصحيحة، تفوز بالنقطة!</li>
            </ol>
          );
        case "yesNo":
          return (
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>كل فريق لديه رمز QR خاص فيه</li>
              <li className="font-semibold">
                فريق واحد يمسح الرمز، والفريق الآخر يسأل أسئلة بنعم أو لا لتخمين
                الشخصية
              </li>
              <li>تبادلوا الأدوار، وكل سؤال يعتبر تخمين</li>
              <li>أول فريق يعرف شخصية الفريق الآخر يفوز!</li>
            </ol>
          );
        case "whoAmI":
          return (
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>الفريق لديه رمز QR واحد فقط</li>
              <li className="font-semibold">
                اختاروا شخص من الفريق يمسح الرمز ويشوف الشخصية
              </li>
              <li>الفريق يسأل اللاعب أسئلة بنعم أو لا لتخمين من هو</li>
              <li>لديكم 30 ثانية، وبعدها كل فريق ثاني عنده 15 ثانية</li>
            </ol>
          );
        case "draw":
          return (
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>اقرأ الكلمة ولا تقولها لأحد</li>
              <li className="font-semibold">
                اضغط "ابدأ الرسم" وارسم الكلمة على جوالك
              </li>
              <li>ممنوع الكلام وممنوع كتابة حروف أو أرقام</li>
              <li>إذا خمّن فريقك الإجابة الصحيحة، تفوز بالنقطة!</li>
            </ol>
          );
        default:
          return null;
      }
    };

    const getTip = () => {
      switch (gameType) {
        case "actItUp":
          return "💡 نصيحة: مثلها عدل عشان فريقك يقدر يجاوب 🏃🏻‍♂️";
        case "forbiddenWords":
          return "💡 نصيحة: إشرح عدل عشان فريقك يقدر يجاوب 🏃🏻‍♂️";
        case "yesNo":
          return "💡 نصيحة: اسألوا أسئلة ذكية عشان تضيّقوا الاحتمالات! 🎯";
        case "whoAmI":
          return "💡 نصيحة: فكروا عدل واسألوا أسئلة تقسّم الاحتمالات! 🕵️";
        case "draw":
          return "💡 نصيحة: ارسم الشكل العام أول وبعدين التفاصيل 🎨";
        default:
          return "";
      }
    };

    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-70 z-40 backdrop-blur-sm"></div>
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
            <div
              className={`p-6 text-white bg-gradient-to-r ${gameConfig.gradient}`}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <GameIcon className="text-3xl" />
                  <h2 className="text-2xl font-bold">{gameConfig.name}</h2>
                </div>
                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all"
                >
                  <FaTimes className="text-white text-xl" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4" dir="rtl">
              <div className="bg-blue-50 border-r-4 border-blue-500 p-4 rounded-lg">
                <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <FaLightbulb className="text-blue-600" />
                  كيفية اللعب:
                </h3>
                {getInstructions()}
              </div>

              <div className="bg-yellow-50 border-r-4 border-yellow-500 p-4 rounded-lg">
                <p className="text-yellow-900 font-medium">{getTip()}</p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 flex justify-end">
              <button
                onClick={() => setShowInstructions(false)}
                className={`bg-gradient-to-r ${gameConfig.gradient} hover:${gameConfig.gradientHover} text-white font-bold py-3 px-8 rounded-xl shadow-md transition-all duration-300`}
              >
                فهمت، لنبدأ!
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // "قول بس لا تقول" Card (Forbidden Words)
  const ForbiddenWordsCard = () => (
    <div className="w-full max-w-2xl mx-auto">
      {/* Game Type Badge */}
      <div className="text-center mb-6">
        <div
          className={`inline-flex items-center gap-3 bg-gradient-to-r ${gameConfig.gradient} text-white px-8 py-4 rounded-2xl shadow-2xl`}
        >
          <GameIcon className="text-3xl" />
          <span className="text-2xl font-bold">{gameConfig.name}</span>
        </div>
      </div>

      {/* Image Section (if exists) - Separated from card */}
      {qrCode.image && (
        <div
          className="mb-6 rounded-2xl overflow-hidden shadow-xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
          onClick={() => setShowImageModal(true)}
        >
          <div className="relative bg-white p-3">
            <div className="relative w-full h-80 bg-gray-100 rounded-xl overflow-hidden">
              <img
                src={qrCode.image}
                alt="Question"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="absolute top-5 right-5 bg-black bg-opacity-60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
              اضغط للتكبير 🔍
            </div>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-6">
        {/* Content Section */}
        <div className="p-8" dir="rtl">
          {/* Question Display */}
          <div className="mb-8">
            <h3 className="text-gray-500 text-lg mb-3 font-medium">
              اشرح هذه الكلمة لفريقك:
            </h3>
            <div
              className={`bg-gradient-to-r ${gameConfig.lightBg} rounded-2xl p-8 border-4 ${gameConfig.border}`}
            >
              <p
                className={`text-4xl md:text-5xl font-bold text-center ${gameConfig.text}`}
              >
                {qrCode.questionId?.question || qrCode.answer}
              </p>
            </div>
          </div>

          {/* Forbidden Words Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <FaBan className="text-2xl text-danger" />
              <h3 className="text-2xl font-bold text-danger">
                الكلمات الممنوعة:
              </h3>
            </div>
            <div className="bg-red-50 rounded-2xl p-6 border-2 border-danger">
              <div className="grid grid-cols-2 gap-4">
                {qrCode.doNotSayWords &&
                  qrCode.doNotSayWords.map((word, index) => (
                    <div
                      key={index}
                      className="bg-white border-2 border-danger rounded-xl p-4 flex items-center justify-center shadow-md"
                    >
                      <div className="flex items-center gap-2">
                        <FaTimes className="text-danger text-xl" />
                        <span className="text-xl font-bold text-danger">
                          {word}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // "ولا كلمة" Card (Act It Up)
  const ActItUpCard = () => (
    <div className="w-full max-w-2xl mx-auto">
      {/* Game Type Badge */}
      <div className="text-center mb-6">
        <div
          className={`inline-flex items-center gap-3 bg-gradient-to-r ${gameConfig.gradient} text-white px-8 py-4 rounded-2xl shadow-2xl`}
        >
          <GameIcon className="text-3xl" />
          <span className="text-2xl font-bold">{gameConfig.name}</span>
        </div>
      </div>

      {/* Image Section (if exists) - Separated from card */}
      {qrCode.image && (
        <div
          className="mb-2 rounded-2xl overflow-hidden shadow-xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
          onClick={() => setShowImageModal(true)}
        >
          <div className="relative bg-white p-3">
            <div className="relative w-full max-h-60 bg-gray-100 rounded-xl overflow-hidden">
              <img
                src={qrCode.image}
                alt="Question"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="absolute top-5 right-5 bg-black bg-opacity-60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
              اضغط للتكبير 🔍
            </div>
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-6">
        {/* Content Section */}
        <div className="p-8" dir="rtl">
          {/* Instruction */}
          <div className="mb-8">
            <h3 className="text-gray-500 text-lg mb-3 font-medium">
              مثّل هذه الكلمة بالحركات فقط:
            </h3>
            <div
              className={`bg-gradient-to-r ${gameConfig.lightBg} rounded-2xl p-8 border-4 ${gameConfig.border}`}
            >
              <p
                className={`text-4xl md:text-5xl font-bold text-center ${gameConfig.text}`}
              >
                {qrCode.questionId?.question || qrCode.answer}
              </p>
            </div>
          </div>

          {/* Rules Section */}
          <div className="mb-6">
            <div
              className={`${gameConfig.bgLight} rounded-2xl p-6 border-2 ${gameConfig.border}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <FaBan className={`text-2xl ${gameConfig.iconColor}`} />
                <h3 className={`text-xl font-bold ${gameConfig.text}`}>
                  القواعد:
                </h3>
              </div>
              <ul className={`space-y-3 ${gameConfig.text}`}>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">🚫</span>
                  <span className="text-lg font-medium">
                    ممنوع الكلام أو إصدار الأصوات
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">✋</span>
                  <span className="text-lg font-medium">
                    استخدم الحركات والإشارات فقط
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-2xl">🎭</span>
                  <span className="text-lg font-medium">
                    مثّل الكلمة بطريقة إبداعية
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // "اي لا" Card (Yes/No Game)
  const YesNoCard = () => {
    return (
      <div className="w-full max-w-2xl mx-auto">
        {/* Game Type Badge */}
        <div className="text-center mb-6">
          <div
            className={`inline-flex items-center gap-3 bg-gradient-to-r ${gameConfig.gradient} text-white px-8 py-4 rounded-2xl shadow-2xl`}
          >
            <GameIcon className="text-3xl" />
            <span className="text-2xl font-bold">{gameConfig.name}</span>
          </div>
        </div>

        {/* Image Section (if exists) */}
        {qrCode.image && (
          <div
            className="mb-6 rounded-2xl overflow-hidden shadow-xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
            onClick={() => setShowImageModal(true)}
          >
            <div className="relative bg-white p-3">
              <div className="relative w-full h-80 bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={qrCode.image}
                  alt="Question"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="absolute top-5 right-5 bg-black bg-opacity-60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                اضغط للتكبير 🔍
              </div>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-6">
          {/* Content Section */}
          <div className="p-8" dir="rtl">
            {/* Important Notice */}
            <div className="mb-8">
              <div className="bg-yellow-50 border-r-4 border-yellow-500 p-4 rounded-lg">
                <p className="text-yellow-900 font-bold text-center text-lg">
                  ⚠️ كل فريق لديه رمز QR مختلف!
                </p>
              </div>
            </div>

            {/* Question Display */}
            <div className="mb-8">
              <h3 className="text-gray-500 text-lg mb-3 font-medium">
                شخصيتك:
              </h3>
              <div
                className={`bg-gradient-to-r ${gameConfig.lightBg} rounded-2xl p-8 border-4 ${gameConfig.border}`}
              >
                <p
                  className={`text-4xl md:text-5xl font-bold text-center ${gameConfig.text}`}
                >
                  {qrCode.questionId?.question || qrCode.answer}
                </p>
              </div>
            </div>

            {/* Rules Section */}
            <div className="mb-6">
              <div
                className={`${gameConfig.bgLight} rounded-2xl p-6 border-2 ${gameConfig.border}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <FaQuestion className={`text-2xl ${gameConfig.iconColor}`} />
                  <h3 className={`text-xl font-bold ${gameConfig.text}`}>
                    كيف تلعب:
                  </h3>
                </div>
                <ul className={`space-y-3 ${gameConfig.text}`}>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">📱</span>
                    <span className="text-lg font-medium">
                      فريق واحد يمسح الرمز ويشوف الشخصية
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">❓</span>
                    <span className="text-lg font-medium">
                      الفريق الآخر يسأل أسئلة بنعم أو لا لتخمين الشخصية
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🔄</span>
                    <span className="text-lg font-medium">
                      تبادلوا الأدوار وكل سؤال يعتبر تخمين
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🏆</span>
                    <span className="text-lg font-medium">
                      أول فريق يعرف شخصية الفريق الآخر يفوز!
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // "من أنا" Card (Who Am I Game)
  const WhoAmICard = () => {
    const startTimer = () => {
      setTimeLeft(30);
      setTimerActive(true);
    };

    const resetTimer = () => {
      setTimeLeft(30);
      setTimerActive(false);
    };

    return (
      <div className="w-full max-w-2xl mx-auto">
        {/* Game Type Badge */}
        <div className="text-center mb-6">
          <div
            className={`inline-flex items-center gap-3 bg-gradient-to-r ${gameConfig.gradient} text-white px-8 py-4 rounded-2xl shadow-2xl`}
          >
            <GameIcon className="text-3xl" />
            <span className="text-2xl font-bold">{gameConfig.name}</span>
          </div>
        </div>

        {/* Image Section (if exists) */}
        {qrCode.image && (
          <div
            className="mb-6 rounded-2xl overflow-hidden shadow-xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
            onClick={() => setShowImageModal(true)}
          >
            <div className="relative bg-white p-3">
              <div className="relative w-full h-80 bg-gray-100 rounded-xl overflow-hidden">
                <img
                  src={qrCode.image}
                  alt="Question"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="absolute top-5 right-5 bg-black bg-opacity-60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                اضغط للتكبير 🔍
              </div>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-6">
          {/* Content Section */}
          <div className="p-8" dir="rtl">
            {/* Timer Display */}
            <div className="mb-8">
              <div
                className={`flex flex-col items-center gap-4 bg-gradient-to-r ${gameConfig.lightBg} rounded-2xl p-8 border-4 ${gameConfig.border}`}
              >
                <FaClock className={`text-6xl ${gameConfig.iconColor}`} />
                <div className={`text-7xl font-bold ${gameConfig.text}`}>
                  {timeLeft}
                </div>
                <p className={`text-xl font-medium ${gameConfig.text}`}>
                  ثانية
                </p>
                <div className="flex gap-3">
                  {!timerActive ? (
                    <button
                      onClick={startTimer}
                      className={`bg-gradient-to-r ${gameConfig.gradient} hover:${gameConfig.gradientHover} text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all duration-300`}
                    >
                      ابدأ العد التنازلي
                    </button>
                  ) : (
                    <button
                      onClick={resetTimer}
                      className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all duration-300"
                    >
                      إعادة تعيين
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="mb-8">
              <div className="bg-blue-50 border-r-4 border-blue-500 p-4 rounded-lg">
                <p className="text-blue-900 font-bold text-center text-lg">
                  💡 الفريق لديه رمز QR واحد فقط - اختاروا شخص ليمسحه!
                </p>
              </div>
            </div>

            {/* Question Display */}
            <div className="mb-8">
              <h3 className="text-gray-500 text-lg mb-3 font-medium">
                الشخصية:
              </h3>
              <div
                className={`bg-gradient-to-r ${gameConfig.lightBg} rounded-2xl p-8 border-4 ${gameConfig.border}`}
              >
                <p
                  className={`text-4xl md:text-5xl font-bold text-center ${gameConfig.text}`}
                >
                  {qrCode.questionId?.question || qrCode.answer}
                </p>
              </div>
            </div>

            {/* Rules Section */}
            <div className="mb-6">
              <div
                className={`${gameConfig.bgLight} rounded-2xl p-6 border-2 ${gameConfig.border}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <FaUserSecret
                    className={`text-2xl ${gameConfig.iconColor}`}
                  />
                  <h3 className={`text-xl font-bold ${gameConfig.text}`}>
                    كيف تلعب:
                  </h3>
                </div>
                <ul className={`space-y-3 ${gameConfig.text}`}>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">👤</span>
                    <span className="text-lg font-medium">
                      شخص واحد من الفريق يمسح الرمز ويشوف الشخصية
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">❓</span>
                    <span className="text-lg font-medium">
                      الفريق يسأل اللاعب أسئلة بنعم أو لا لتخمين من هو
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">⏱️</span>
                    <span className="text-lg font-medium">
                      30 ثانية للفريق، ثم 15 ثانية لكل فريق آخر
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-2xl">🤫</span>
                    <span className="text-lg font-medium">
                      اللاعب الذي مسح الرمز لا يُظهر شاشته للفريق
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render the appropriate card based on game type
  const renderGameCard = () => {
    switch (gameType) {
      case "actItUp":
        return <ActItUpCard />;
      case "forbiddenWords":
        return <ForbiddenWordsCard />;
      case "yesNo":
        return <YesNoCard />;
      case "whoAmI":
        return <WhoAmICard />;
      case "draw":
        return (
          <DrawerPanel
            qrCode={qrCode}
            code={drawCode}
            gameConfig={gameConfig}
          />
        );
      default:
        return <ActItUpCard />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-behindButton to-background font-sans select-none">
      {/* Header */}
      <div className="bg-gradient-to-br from-header via-background to-behindButton text-white py-6 px-4 shadow-lg sticky top-0 z-30 backdrop-blur-sm bg-opacity-95">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <Link
            to="/"
            className="flex items-center gap-2 text-white hover:text-gray-200 transition-colors"
          >
            <FaArrowLeft className="text-xl" />
            <span className="font-bold">العودة</span>
          </Link>
          <img src={logo} alt="تربع" className="h-12" />
          <button
            onClick={() => setShowInstructions(true)}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-all font-bold backdrop-blur-sm"
          >
            التعليمات
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 px-4">{renderGameCard()}</div>

      {/* Instructions Modal */}
      {showInstructions && <InstructionsModal />}

      {/* Image Modal */}
      {showImageModal && qrCode?.image && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-90 z-50 backdrop-blur-sm"
            onClick={() => setShowImageModal(false)}
          ></div>
          <div
            onClick={() => setShowImageModal(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <button
                onClick={() => setShowImageModal(false)}
                className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all backdrop-blur-sm z-10"
              >
                <FaTimes className="text-white text-2xl" />
              </button>
              <div className="relative max-h-full w-full h-full flex items-center justify-center ">
                <img
                  src={qrCode.image}
                  alt="Full size"
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  onClick={() => setShowImageModal(false)}
                />
              </div>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white text-sm px-4 py-2 rounded-full backdrop-blur-sm">
                اضغط في أي مكان للإغلاق
              </div>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="text-center text-white py-6 px-4">
        <p className="text-sm opacity-80">
          © {new Date().getFullYear()} تربع - جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
};

export default QRCodeGame;
